/**
 * Строка дня: сто советов о том, КАК работать.
 *
 * **Где здесь граница, за которую нельзя.** Тон системы — числа вместо похвалы,
 * и он не тронут. Похвала это оценка человека («Молодец», «ты умница»): она
 * привязывает продолжение к чужому одобрению, и значков с наградами за
 * количество в системе нет по измеренной причине (d = −0,44). Совет о действии
 * человека не оценивает вовсе. Тот же довод, по которому в системе живут
 * двадцать четыре заголовка вердикта: они говорят о задаче, а не об ученике.
 *
 * Проверка простая: если строку можно сказать тому, кто сегодня ничего не
 * решил, и она не прозвучит ни упрёком, ни лестью — строка годится.
 *
 * **Почему пять корзин, а не один список.** Корзина — это и есть будущая
 * привязка к состоянию: после ошибки берётся из `miss`, после долгого перерыва
 * из `memory`, в день первого нового навыка из `method`. В фазе 1 привязки нет,
 * выбор идёт по всем ста — мотивационный слой это фаза 4, и строить его сейчас
 * значило бы строить то, чего в фазе нет. Но корзины стоят уже сейчас, чтобы
 * при привязке не перечитывать сто строк заново.
 *
 * Восклицательных знаков нет ни одного: знак на каждый день — та же звёздочка,
 * только текстом. Длину и тон держит `tests/advice-lines.test.ts`.
 */

/** Корзина совета. Она же — будущее состояние, по которому его выберут. */
export type AdviceTopic = "miss" | "memory" | "focus" | "method" | "exam";

/** Длина под вёрстку карточки: две строки при 15px, без переноса на третью. */
export const ADVICE_LIMIT = 120;

export const ADVICE: Record<AdviceTopic, readonly string[]> = {
  /** Работа с ошибкой. Самая ценная секунда системы — та, что после промаха. */
  miss: [
    "Ошибся — не перерешивай сразу. Сначала найди, где свернул: иначе повторишь тот же поворот.",
    "Разбор читай до конца, даже когда понял на втором шаге. Ошибка редко там, где её ждёшь.",
    "После ошибки скажи вслух, в чём она была. Не смог сказать — значит ещё не понял.",
    "Неверный ответ дороже верного: он показывает границу знания, верный только подтверждает.",
    "Ошибка в арифметике — всё равно ошибка. На экзамене её не отличают от незнания темы.",
    "Одна и та же ошибка дважды — не случайность. Запомни её признак, а не только исправление.",
    "Не спорь с разбором молча. Найди место, где твой ход и его ход разошлись.",
    "Ошибка на тренировке стоит минуту. Та же ошибка на экзамене стоит балл.",
    "Перечитай своё решение, как чужое. Своё читается тем, что ты хотел написать.",
    "Ответ не сошёлся — проверь сначала условие. Половина промахов в том, что прочитал не то.",
    "Потерял знак — обведи это место. Знаки теряются там же, где терялись в прошлый раз.",
    "Не исправляй ответ наугад. Наугад верный ответ не учит ничему.",
    "Разбор кажется очевидным — закрой его и реши заново. Узнавание это ещё не умение.",
    "Ошибку в вычислении ищи с конца: последнее действие ломается чаще первого.",
    "Задача, решённая со второго раза, ещё не закрыта. Её близнец придёт позже.",
    "Понял, где ошибся, — это половина. Вторая половина в том, почему ты пошёл туда.",
    "Не выбрасывай черновик после ошибки. По нему видно, на каком шаге ты перестал думать.",
    "Промах в лёгкой задаче важнее промаха в трудной: лёгкие ты будешь решать на автомате.",
    "Не понял разбор — это не про способности, это про навык ниже. Он найдётся по карте.",
    "Проверь ответ подстановкой, прежде чем отправить. Это дешевле, чем потом читать разбор.",
  ],
  /** Повторение и забывание. Против ощущения, что перечитанное — выученное. */
  memory: [
    "Повторение приходит, когда ты почти забыл. В этом весь смысл: лёгкое повторение не работает.",
    "Забыл формулу — не подглядывай сразу. Тридцать секунд попытки вспомнить стоят трёх прочтений.",
    "Выученное за вечер забывается за неделю. Разложенное на неделю держится месяцами.",
    "Повторять решённое скучно и полезно. Решать новое интересно и недостаточно.",
    "Тема кажется знакомой — это ещё не значит, что ты её решишь.",
    "Пропущенный день не ломает ничего. Долг просто вернётся, и набор станет плотнее.",
    "Вспомнить самому сильнее, чем перечитать. Даже если вспомнил с третьей попытки.",
    "Тему, которую не трогал месяц, надо не повторить, а прорешать.",
    "Перечитывание даёт ощущение знания, а не знание. Проверяется оно только задачей.",
    "Навык держится не тем, сколько ты его учил, а тем, сколько раз к нему возвращался.",
    "Открыл много тем разом — через неделю они вернутся все вместе. Это не сбой, это цена.",
    "Интервал между повторениями растёт, пока ты отвечаешь верно. Ошибка его сбрасывает.",
    "Пятнадцать минут каждый день сильнее двух часов в воскресенье.",
    "Помнишь формулу, но не помнишь, откуда она, — выведи её один раз.",
    "Старая тема всплывает внутри новой. Это не отвлечение, это и есть повторение.",
    "Не откладывай то, что почти забыл. Именно на этом рубеже повторение и работает.",
    "Задача из прошлой недели проверяет память. Задача из сегодняшней проверяет понимание.",
    "Не бойся вернуться к пятому классу. Дыра внизу дороже любой темы наверху.",
    "Знание, которое приходит только с подсказкой, ещё не твоё.",
    "Скорость придёт от повторений, а не от спешки.",
  ],
  /** Как идёт занятие: внимание, усталость, начало и обрыв. */
  focus: [
    "Начни с задачи, а не с планов на вечер. Первая решённая включает остальные.",
    "Пятнадцать минут внимания дают больше часа с телефоном рядом.",
    "Телефон в другую комнату, а не экраном вниз. Экраном вниз он всё равно слышен.",
    "Устал — останови набор, а не растягивай его на три часа фоном.",
    "Трудная задача после лёгкой запоминается лучше, чем три лёгкие подряд.",
    "Не начинай с самой трудной. Начни с той, по которой понятно первое действие.",
    "Сидишь над задачей десять минут без движения — открой подсказку. Двадцать минут дороже.",
    "Считай в тетради, а не в голове. Голова нужна на метод, а не на хранение чисел.",
    "Перерыв на пять минут, а не на пять постов. Второе не перерыв.",
    "Занятие в одно и то же время требует меньше воли, чем занятие «когда получится».",
    "Нет настроения — сделай минимум. Сделанный минимум лучше отложенного набора.",
    "Не считай, сколько осталось. Считай, что делаешь сейчас.",
    "Тянет бросить на середине — закрой одну задачу и тогда решай, бросать ли.",
    "Музыка со словами занимает ту же часть головы, что и текст задачи.",
    "Решать в шуме можно. Разбираться в новом — нет.",
    "Два коротких занятия в день сильнее одного длинного той же длины.",
    "Не держи вкладку с набором открытой «на потом». Либо решаешь, либо закрываешь.",
    "Не готовься к занятию. Занятие — это первая задача, а не порядок на столе.",
    "Сонным решать бессмысленно: ошибки пойдут в арифметике, а разбор будет про метод.",
    "Начатое занятие почти всегда доводится до конца. Трудно начать, а не продолжить.",
  ],
  /** Ход решения: что делать до, во время и после вычислений. */
  method: [
    "Прежде чем считать, скажи, что ищешь. Половина решений ломается на этом шаге.",
    "Запиши, что дано, даже когда кажется, что помнишь.",
    "Не начинай с формулы. Начни с вопроса, к какому типу относится задача.",
    "Метод не виден — попробуй частный случай с маленькими числами.",
    "Проверяй ответ на здравый смысл: скорость 700 км/ч у пешехода — сигнал, а не опечатка.",
    "Промежуточные вычисления не выбрасывай. По ним потом ищется ошибка.",
    "Раскрыть скобки — не всегда первый шаг. Иногда выгоднее вынести общий множитель.",
    "Прежде чем делить, спроси себя, не ноль ли делитель.",
    "Одна задача — один лист. Решение на полях теряется вместе с ошибкой.",
    "Выражение громоздкое — обозначь кусок буквой. Половина трудности в записи, а не в математике.",
    "Дроби приведи к общему знаменателю раньше, чем начнёшь упрощать.",
    "Дробь в ответе — нормальный ответ. Десятичная запись чаще теряет точность.",
    "Минус перед скобкой меняет всё внутри. Это место проверяй отдельно.",
    "Решение растянулось на полстраницы — скорее всего, метод не тот.",
    "Формулу, которой пользуешься, проверь на примере, где ответ известен.",
    "Подстановка в исходное уравнение — не лишняя работа, а последний шаг решения.",
    "Не переходи к следующему действию, пока текущее не записано целиком.",
    "Заметил закономерность — проверь её ещё на одном примере, прежде чем строить на ней.",
    "Красивое число в ответе — хороший знак, но не доказательство.",
    "Оформляй так, чтобы через неделю ты сам понял свой ход.",
  ],
  /** Подготовка к июню: что на экзамене работает, а что только кажется. */
  exam: [
    "Экзамен проверяет не то, что ты знаешь, а то, что достанешь из памяти под часами.",
    "Задача формата экзамена стоит в наборе каждый день, чтобы к июню формат не был новостью.",
    "К июню важно не число решённых задач, а число тем, которые не проваливаются.",
    "На экзамене подсказок нет. Поэтому счёт идёт по задачам, решённым без них.",
    "Тема, пройденная на семьдесят процентов, на экзамене работает как непройденная.",
    "Время на экзамене — тоже условие задачи. Быстро решать учатся повторением, а не спешкой.",
    "Двадцать тем на прочность лучше шестидесяти на узнавание.",
    "Пробник показывает не оценку, а список того, что чинить.",
    "Экзамен пишут не самые способные, а самые подготовленные. Разница в числе повторений.",
    "Дыра в фундаменте всплывает на экзамене внутри самой трудной задачи.",
    "Учить новое за три недели до экзамена дороже, чем закрепить старое.",
    "Задача, решённая однажды, на экзамене выходит не всегда. Решённая пять раз — почти всегда.",
    "Списать формулу на экзамене не выйдет. Вывести — иногда да.",
    "Формат задания надо узнавать с первой строки. Это экономит минуты, которых не хватит.",
    "Не сравнивай себя с тем, кто начал раньше. Сравнивай с собой месяц назад.",
    "Балл складывается из задач, которые решаешь уверенно, а не из тех, что почти получаются.",
    "Ошибка в простом действии стоит ровно столько же, сколько незнание темы.",
    "За три недели до экзамена новые темы не открываются — так решено заранее.",
    "Каждый открытый навык вернётся повторениями. Это цена, а не наказание.",
    "Дни до экзамена считаются для планирования, а не для тревоги.",
  ],
};

/** Все сто строк одним списком, в порядке корзин. */
export const ADVICE_LINES: readonly string[] = [
  ...ADVICE.miss,
  ...ADVICE.memory,
  ...ADVICE.focus,
  ...ADVICE.method,
  ...ADVICE.exam,
];

/**
 * Новая строка на каждое открытие страницы.
 *
 * Сначала строка была одна на день — перестановка от ключа ученика и даты, без
 * хранения. Решение пользователя от 14 сентября 2026: менять на каждой
 * перезагрузке. Это возвращает исходную идею («появляются рандомно»),
 * и цена честная: строка перестаёт быть «мыслью дня» и становится сменной.
 *
 * Выбирается ОДИН раз на монтирование экрана, а не на каждую перерисовку:
 * строка, скачущая от нажатия на любую кнопку, читается как шум, а не как
 * совет. За это отвечает вызывающий — `useState` с ленивым начальным
 * значением.
 *
 * Хранить последнюю показанную незачем: строк сто, и повтор подряд выпадает раз
 * из ста. Ради этого одного случая заводить запись в localStorage, которая
 * умеет бросать в приватном окне, — плохой размен.
 */
export function pickAdvice(random: () => number = Math.random): string {
  const i = Math.floor(random() * ADVICE_LINES.length);
  return ADVICE_LINES[Math.min(Math.max(i, 0), ADVICE_LINES.length - 1)]!;
}
